<template>
  <div class="upload-image">
    <el-upload class="upload-el" :style="'text-align: ${position};'" accept="image/*" ref="fileUpload" name="file" :action="uploadUrl"
      :on-change="handleFileChange" :show-file-list="false" :auto-upload="false" :http-request="handleHttpRequest">
      <el-button v-if="showUploadBtn" type="primary" :disabled="disabled" icon="el-icon-upload2" @click="handleUploadBtnClick">
        {{ buttonText }}
      </el-button>
    </el-upload>
    <cropper v-if="enableCropper" :dialog-visible="showCropper" :cropper-img="cropperImg"
      @close-dialog="handleCloseDialog" @upload-img="handleUploadImg" />
  </div>
</template>

<script>
import Cropper from "./cropper.vue";
import axios from "axios";
import { loading } from "./common";
import { getToken } from "@/utils/auth";
import { Encrypt, Decrypt } from '@/utils/index.js'
import { SHA256 } from 'crypto-js'

export default {
  name: "UploadImage",
  components: {
    Cropper
  },
  props: {
    // 是否显示上传按钮
    showUploadBtn: {
      type: Boolean,
      default: true
    },
    // 上传按钮文字
    position: {
      type: String,
      default: 'center'
    },
    // 是否禁用上传
    disabled: {
      type: Boolean,
      default: false
    },
    // 是否启用裁剪
    enableCropper: {
      type: Boolean,
      default: true
    },
    // 按钮文字
    buttonText: {
      type: String,
      default: '上传图片'
    },
    // 最大文件大小（单位：B）
    maxFileSize: {
      type: Number,
      default: 2 * 1024 * 1024
    },
    // 上传URL
    uploadUrl: {
      type: String,
      default: process.env.VUE_APP_BASE_API + "/api/out/minio/upload"
    },
    // 上传参数
    uploadParams: {
      type: Object,
      default: () => ({ bucketType: 1 })
    }
  },
  data() {
    return {
      cropperImg: "",
      showCropper: false,
      uploadFile: "",
      triggerParams: null, // 触发上传的参数
    };
  },
  methods: {
    handleUploadBtnClick() {
      this.triggerParams = null;
      this.$refs.fileUpload.clearFiles();
    },
    // 处理文件选择
    handleFileChange(file) {
      if (this.disabled) {
        return;
      }
      const { raw } = file;
      if (this.enableCropper) {
        this.openCropper(raw);
      } else {
        this.uploadFile = raw;
        this.$refs.fileUpload.submit();
      }
    },
    // 打开裁剪框
    openCropper(file) {
      const reader = new FileReader();
      reader.onload = (e) => {
        let data;
        if (typeof e.target.result === "object") {
          data = window.URL.createObjectURL(new Blob([e.target.result]));
        } else {
          data = e.target.result;
        }
        this.cropperImg = data;
        this.showCropper = true;
      };
      reader.readAsArrayBuffer(file);
    },
    // 处理HTTP请求
    handleHttpRequest(request) {
      const checkSize = this.uploadFile.size > this.maxFileSize;
      if (checkSize) {
        // 提示文件大小保留两位小数位，根据值判断显示B\KB\MB
        if (this.maxFileSize < 1024) {
          this.$message.error("上传图片大小不能超过" + this.maxFileSize + "B"); 
        } else if (this.maxFileSize < 1024 * 1024) {
          this.$message.error("上传图片大小不能超过" + (this.maxFileSize / 1024) + "KB");
        } else {
          this.$message.error("上传图片大小不能超过" + (this.maxFileSize / 1024 / 1024) + "MB");
        }
        return;
      }

      loading.start();
      const { filename } = request;
      const formData = new FormData();
      const params = this.uploadParams;
      for (const key in params) {
        formData.append(key, params[key]);
      }
      const files = new window.File([this.uploadFile], request.file.name);
      formData.append(filename, files, request.file.name);

      const time = Date.now();
      const obj = { token: Decrypt(getToken()), timestamp: time };
      axios({
        url: this.uploadUrl,
        method: "post",
        data: formData,
        timeout: 200000,
        headers: {
          'signature': SHA256('timestamp' + time + process.env.VUE_APP_BASE_signature).toString(),
          'timestamp': time,
          'Token': Encrypt(JSON.stringify(obj)),
        },
      })
        .then((res) => {
          if (res.data.status_code == 200) {
            this.$message.success("图片上传成功");
            this.$emit('upload-success', res.data.data, this.triggerParams);
          } else {
            this.$message.error("图片上传失败");
            this.$emit('upload-error', res.data, this.triggerParams);
          }
        })
        .catch((err) => {
          console.log(err);
          this.$message.error("图片上传失败");
          this.$emit('upload-error', err, this.triggerParams);
        })
        .finally(() => {
          loading.close();
          this.resetState();
        });
    },
    // 处理裁剪后的上传
    handleUploadImg(file) {
      if (!file) {
        return;
      }
      this.uploadFile = file;
      this.$refs.fileUpload.submit();
    },
    // 处理关闭对话框
    handleCloseDialog() {
      this.resetState();
    },
    // 重置状态
    resetState() {
      this.$refs.fileUpload.clearFiles();
      this.uploadFile = null;
      this.showCropper = false;
      this.cropperImg = "";
    },
    // 触发上传
    triggerUpload(params) {
      this.triggerParams = params;
      this.$refs.fileUpload.$children[0].$el.click();
    }
  },
  // 暴露给父组件调用的方法
  expose: ['triggerUpload']
};
</script>

<style lang="scss" scoped>
.upload-image {
  .upload-el {
    width: 100%;
    height: 100%;
  }
}
</style>